import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';

import { Client } from 'src/providers/client/client';
import { Aws } from 'src/providers/aws/aws';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { FileUploadComponent } from 'src/tidy-ui-components/components/file-upload/file-upload.component';
import { randomUUID } from 'src/tidy-ui-components/utils/secure-utils';

// One photo in the `property_photos_inputs` snapshot. `url` is the public S3 URL
// (same direct-upload bucket chat attachments use); `key` is the S3 object key.
interface PropertyPhotoInput {
  url: string;
  key: string;
  filename: string | null;
}

/**
 * "Add Property Photos" onboarding step (Funnel V2). Surfaced when a concierge operator
 * assigns the `property-photos` item to the client, or self-assigned by the Connect
 * Listings wizard when the client says they have no listing yet.
 *
 * Two-step wizard (progress bar + back, like the Restocking step): step 1 asks whether
 * the client has photos we can use for their listing; step 2 is either the photo picker
 * (each photo re-encoded to JPEG via canvas, see normalizePhoto, and uploaded straight to
 * S3 via the existing {@link Aws} provider — the same client-credential upload path chat
 * attachments use) or, when they have none, whether TIDY should hire a photographer.
 * Submit writes two metadata rows:
 * - `onboarding_step.{addressId}.property_photos_inputs` — JSON snapshot of the answers +
 *   uploaded photo URLs, rendered by the concierge board's "View Inputs" dialog.
 * - `onboarding_step.{addressId}.property-photos = completed` — the client-visible signal
 *   the concierge board reads back as "done".
 */
@Component({
  selector: 'property-photos-onboarding',
  templateUrl: './property-photos-onboarding.component.html',
  styleUrls: ['./property-photos-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class PropertyPhotosOnboardingComponent implements OnInit, OnDestroy {
  private client = inject(Client);
  private aws = inject(Aws);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  windowService = inject(WindowService);

  @ViewChild('photoPicker') private photoPicker: FileUploadComponent;

  // Fixed id for the picker's hidden <input type="file"> so the visible tidy-button
  // can trigger it on web; native goes through the component's pickPhotos() instead.
  readonly pickerInputId = 'property-photos-file-input';

  private addressId: number;
  private readonly stepId = 'property-photos';

  currentStep: 1 | 2 = 1;

  // "Do you have photos we can use for your listing?" — null until answered.
  hasPhotos: boolean | null = null;
  // "Do you want us to hire a photographer?" — asked only when hasPhotos is false.
  hirePhotographer: boolean | null = null;

  readonly hasPhotosOptions = [
    { value: true, viewValue: 'Yes' },
    { value: false, viewValue: 'No' },
  ];
  readonly photographerOptions = [
    { value: true, viewValue: 'Yes' },
    { value: false, viewValue: 'No, I will get my own photographer' },
  ];

  // Preview entries for tidy-file-upload. New picks carry {url: JPEG dataURL, file, exif};
  // photos already uploaded on a previous submit carry {url: S3 URL, key} instead and
  // are skipped at upload time.
  photos: any[] = [];
  loaded = false;
  isSubmitting = false;
  fromGettingStarted = false;

  get progressPercent(): number {
    return Math.round((this.currentStep / 2) * 100);
  }

  private get inputsKey(): string {
    return `onboarding_step.${this.addressId}.property_photos_inputs`;
  }

  private get stepKey(): string {
    return `onboarding_step.${this.addressId}.${this.stepId}`;
  }

  get canSubmit(): boolean {
    if (this.hasPhotos === true) { return this.photos.length > 0; }
    if (this.hasPhotos === false) { return this.hirePhotographer !== null; }
    return false;
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.rightSidePanelService.setDialogPageTitle('Add Property Photos');
    await this.loadExistingInputs();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  // A prior submission's answers + photos are restored (landing on step 2, like the
  // Restocking wizard) so the client edits what they sent instead of starting over.
  // Photo entries the browser can't actually render (e.g. HEIC uploaded before
  // re-encoding existed) are dropped rather than shown as broken images.
  private async loadExistingInputs(): Promise<void> {
    try {
      const rows: any[] = await this.client.getMetadata([`external.${this.inputsKey}`]);
      const row = (Array.isArray(rows) ? rows : []).find((r) => r?.value);
      if (!row) { return; }
      const snapshot = JSON.parse(row.value);
      const existing: PropertyPhotoInput[] = Array.isArray(snapshot?.photos) ? snapshot.photos : [];
      const entries = existing
        .filter((p) => !!p?.url)
        .map((p) => ({
          uuid: p.key || p.url,
          url: p.url,
          key: p.key,
          filename: p.filename ?? null,
          mediaType: 'photo',
        }));
      const renderable = await Promise.all(
        entries.map((e) => this.canRenderUrl(e.url).then((ok) => (ok ? e : null)))
      );
      this.photos = renderable.filter(Boolean);
      if (typeof snapshot?.hasPhotos === 'boolean') {
        this.hasPhotos = snapshot.hasPhotos;
        this.hirePhotographer = typeof snapshot?.hirePhotographer === 'boolean' ? snapshot.hirePhotographer : null;
      } else if (this.photos.length) {
        // Legacy snapshot without answers — photos present implies "yes".
        this.hasPhotos = true;
      }
      if (this.hasPhotos !== null) {
        this.goToStep(2);
      }
    } catch {
      // Missing or malformed snapshot — start the wizard fresh.
    }
  }

  selectHasPhotos = (value: boolean) => {
    this.hasPhotos = value;
    if (value) { this.hirePhotographer = null; }
    this.goToStep(2);
  };

  selectPhotographer = (value: boolean) => {
    this.hirePhotographer = value;
    this.cdr.markForCheck();
  };

  private goToStep(step: 1 | 2) {
    this.currentStep = step;
    this.updateBackAction();
    this.cdr.markForCheck();
  }

  private updateBackAction() {
    if (this.currentStep > 1) {
      this.rightSidePanelService.customBackAction$.next(() => {
        this.goToStep(1);
      });
    } else {
      this.rightSidePanelService.customBackAction$.next(null);
    }
  }

  openPhotoPicker = () => {
    if (Capacitor.isNativePlatform()) {
      this.photoPicker?.pickPhotos();
    } else {
      (document.getElementById(this.pickerInputId) as HTMLInputElement | null)?.click();
    }
  };

  onPhotosAdded = async (added: any[]) => {
    const normalized = await Promise.all((added || []).map((image) => this.normalizePhoto(image)));
    const kept = normalized.filter(Boolean);
    if (kept.length < (added || []).length) {
      this.util.showError('Some photos couldn\'t be read by this browser. Please use JPG or PNG files.');
    }
    this.photos = [...this.photos, ...kept];
    this.cdr.markForCheck();
  };

  onPhotoRemoved = (event: { index: number; image: any }) => {
    this.photos = this.photos.filter((_, i) => i !== event.index);
    this.cdr.markForCheck();
  };

  onFileError = (error: any) => {
    this.util.showError(typeof error === 'string' ? error : 'Failed to add that file. Please try again.');
  };

  submit = async () => {
    if (!this.canSubmit || this.isSubmitting) { return; }
    this.isSubmitting = true;
    this.cdr.markForCheck();
    try {
      const uploaded: PropertyPhotoInput[] = [];
      if (this.hasPhotos) {
        for (const photo of this.photos) {
          if (!photo.key) {
            const key = `property-photos/${this.addressId}/${photo.uuid || randomUUID()}.jpg`;
            const response = await this.aws.uploadFileToS3(photo.url, key, 'image/jpeg', photo.file, photo.exif);
            // Stash the result on the entry so a retry after a later failure doesn't re-upload.
            photo.key = response.Key;
            photo.url = response.Location;
            photo.filename = photo.file?.name || null;
          }
          uploaded.push({ url: photo.url, key: photo.key, filename: photo.filename ?? null });
        }
      }
      await this.client.addMetadata({
        key: this.inputsKey,
        value: JSON.stringify({
          v: 1,
          submittedAt: new Date().toISOString(),
          hasPhotos: this.hasPhotos,
          hirePhotographer: this.hasPhotos ? null : this.hirePhotographer,
          photos: uploaded,
        }),
      });
      await this.client.addMetadata({ key: this.stepKey, value: 'completed' });
      this.util.showSuccess('Submitted!');
      this.navigateBack();
    } catch (err) {
      this.util.showError('Failed to submit. Please try again.');
    } finally {
      this.isSubmitting = false;
      this.cdr.markForCheck();
    }
  };

  // Re-encode a picked photo to a JPEG data URL via canvas (capped at 1600px). This
  // guarantees the preview, the uploaded file, and the concierge "View Inputs" grid
  // render in any browser — an iPhone HEIC picked on iOS decodes natively in the
  // webview and comes out as JPEG here. Resolves null when the browser can't decode
  // the file at all (e.g. HEIC on desktop Chrome), so the caller can drop it with a
  // clear error instead of showing a broken image.
  private normalizePhoto(image: any): Promise<any | null> {
    return new Promise((resolve) => {
      if (!image?.url) { resolve(null); return; }
      const img = new Image();
      img.onload = () => {
        try {
          const MAX_DIMENSION = 1600;
          const width = img.naturalWidth || 1;
          const height = img.naturalHeight || 1;
          const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(width * scale));
          canvas.height = Math.max(1, Math.round(height * scale));
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve({ ...image, url: canvas.toDataURL('image/jpeg', 0.9) });
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = image.url;
    });
  }

  private canRenderUrl(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  private navigateBack() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('get-started-status');
    }
  }
}
